// Copyright (c) Microsoft. All rights reserved.

import { useMsal } from '@azure/msal-react';
import { useAppDispatch, useAppSelector } from '../../redux/app/hooks';
import { FeatureKeys } from '../../redux/features/app/AppState';
import { addAlert, toggleFeatureState } from '../../redux/features/app/appSlice';
import { AuthHelper } from '../auth/AuthHelper';
import { DocumentImportService } from '../services/DocumentImportService';
import { getErrorDetails } from '../../components/utils/TextUtils';
import { AlertType } from '../models/AlertType';
import { RootState } from '../../redux/app/store';
import { FileService } from '../services/FileService';

export const useFile = () => {
    const dispatch = useAppDispatch();
    const { instance, inProgress } = useMsal();
    const { features } = useAppSelector((state: RootState) => state.app);

    const documentImportService = new DocumentImportService();
    const fileService = new FileService();

    async function loadFile<T>(file: File, loadCallBack: (data: T) => Promise<void>): Promise<T> {
        return await new Promise((resolve, reject) => {
            const fileReader = new FileReader();
            fileReader.onload = async (event: ProgressEvent<FileReader>) => {
                const content = event.target?.result as string;
                try {
                    const parsedData = JSON.parse(content) as T;
                    await loadCallBack(parsedData);
                    resolve(parsedData);
                } catch (e) {
                    reject(e);
                }
            };
            fileReader.onerror = reject;
            fileReader.readAsText(file);
        });
    }

    function downloadFile(filename: string, content: string, type: string) {
        const data: BlobPart[] = [content];
        let file: File | null = new File(data, filename, { type });

        const link = document.createElement('a');
        link.href = URL.createObjectURL(file);
        link.download = filename;

        link.click();
        URL.revokeObjectURL(link.href);
        link.remove();
        file = null;
    }

    const handleDocumentImport = async (documentFileRef: React.MutableRefObject<HTMLInputElement | null>) => {
        const files = documentFileRef.current?.files;
        if (files && files.length > 0) {
            // Deep copy the FileList into an array so that the function
            // maintains a list of files to import before the import is complete.
            const filesArray = Array.from(files);

            if (filesArray.length > 0) {
                await importDocument(filesArray);
            }
        }

        // Reset the file input so that the onChange event will
        // be triggered even if the same file is selected again.
        if (documentFileRef.current?.value) {
            documentFileRef.current.value = '';
        }
    };

    const importDocument = async (files: File[]) => {
        try {
            await documentImportService.importDocumentAsync(
                files,
                features[FeatureKeys.AzureContentSafety].enabled,
                await AuthHelper.getSKaaSAccessToken(instance, inProgress),
            );
            dispatch(addAlert({ message: 'Document(s) uploaded successfully.', type: AlertType.Success }));
        } catch (e: any) {
            let errorDetails = getErrorDetails(e);

            // Disable Content Safety if request was unauthorized
            const contentSafetyDisabledRegEx = /Access denied: \[Content Safety] Failed to analyze image./g;
            if (contentSafetyDisabledRegEx.test(errorDetails)) {
                if (features[FeatureKeys.AzureContentSafety].enabled) {
                    errorDetails =
                        'Unable to analyze image. Content Safety is currently disabled or unauthorized service-side. Please contact your admin to enable.';
                }

                dispatch(
                    toggleFeatureState({ feature: FeatureKeys.AzureContentSafety, deactivate: true, enable: false }),
                );
            }

            const errorMessage = `Failed to upload document(s). Details: ${errorDetails}`;
            dispatch(addAlert({ message: errorMessage, type: AlertType.Error }));
        }
    };

    const getContentSafetyStatus = async () => {
        try {
            const result = await documentImportService.getContentSafetyStatusAsync(
                await AuthHelper.getSKaaSAccessToken(instance, inProgress),
            );

            if (result) {
                dispatch(
                    toggleFeatureState({ feature: FeatureKeys.AzureContentSafety, deactivate: false, enable: true }),
                );
            }
        } catch (error) {
            /* Do nothing, leave feature disabled */
        }
    };

    const openDocument = async (documentId: string, fileName: string) => {
        try {
            /* eslint-disable-next-line padded-blocks */
            const url = await documentImportService.getDocumentUrlAsync(
                documentId,
                fileName,
                await AuthHelper.getSKaaSAccessToken(instance, inProgress),
            );

            if (url) {
                /* eslint-disable-next-line padded-blocks */
                const link = document.createElement('a');
                link.href = url;
                link.target = '_blank';
                link.click();
            }
        } catch (error) {
            /* Do nothing, leave feature disabled */
        }
    };

    const listDocuments = async () => {
        return documentImportService.listDocumentsAsync(await AuthHelper.getSKaaSAccessToken(instance, inProgress));
    };

    const deleteDocuments = async (documentIds: string[]) => {
        const accessToken = await AuthHelper.getSKaaSAccessToken(instance, inProgress);

        await Promise.all(
            documentIds.map((documentId) => {
                return documentImportService.deleteDocumentAsync(documentId, accessToken);
            }),
        );

        dispatch(addAlert({ message: 'Document(s) deleted successfully.', type: AlertType.Success }));
    };

    /***
     * FILES
     */

    const handleFileImport = async (uploadFileRef: React.MutableRefObject<HTMLInputElement | null>) => {
        const files = uploadFileRef.current?.files;
        if (files && files.length > 0) {
            // Deep copy the FileList into an array so that the function
            // maintains a list of files to import before the import is complete.
            const filesArray = Array.from(files);

            if (filesArray.length > 0) {
                await importFile(filesArray);
            }
        }

        // Reset the file input so that the onChange event will
        // be triggered even if the same file is selected again.
        if (uploadFileRef.current?.value) {
            uploadFileRef.current.value = '';
        }
    };

    const importFile = async (files: File[]) => {
        try {
            await fileService.importFileAsync(files, await AuthHelper.getSKaaSAccessToken(instance, inProgress));
            dispatch(addAlert({ message: 'Document(s) uploaded successfully.', type: AlertType.Success }));
        } catch (e: any) {
            const errorDetails = getErrorDetails(e);
            const errorMessage = `Failed to upload document(s). Details: ${errorDetails}`;
            dispatch(addAlert({ message: errorMessage, type: AlertType.Error }));
        }
    };

    const listFiles = async () => {
        return fileService.listFilesAsync(await AuthHelper.getSKaaSAccessToken(instance, inProgress));
    };

    const deleteFiles = async (fileNames: string[]) => {
        const accessToken = await AuthHelper.getSKaaSAccessToken(instance, inProgress);

        await Promise.all(
            fileNames.map((fileName) => {
                return fileService.deleteFileAsync(fileName, accessToken);
            }),
        );

        dispatch(addAlert({ message: 'File(s) deleted successfully.', type: AlertType.Success }));
    };

    return {
        loadFile,
        downloadFile,
        handleDocumentImport,
        getContentSafetyStatus,
        openDocument,
        listDocuments,
        deleteDocuments,

        handleFileImport,
        listFiles,
        deleteFiles,
    };
};
